/* Try it on before you buy it.
   ------------------------------------------------------------------------
   Shopify's pickup line says a shop can hand this order over. It does not
   say whether the size on the screen is on that shop's shelf today, which
   is the only question a customer deciding whether to make the trip is
   actually asking. Our own API answers it per size and per shop.

   Three things come out of that answer:
     · a dot on every size a customer could walk in and try;
     · a line about the size they have chosen, with the shop's address;
     · a fitting they can ask for, which lands in Hive as a ticket the team
       answers like any other message. No diary, no slots — the hour is
       agreed in the reply, because an empty diary is worse than none.

   Everything here is additive. A failed fetch leaves the page exactly as it
   was rendered, which is the page as it shipped before this file existed. */
(function () {
  'use strict';

  var EN = {
    tryOn: 'Try this on',
    onShelf: 'On the shelf',
    shelfAt: 'Your {size} is on the shelf at {shop}',
    lastOne: 'Last one in this size.',
    someLeft: '{count} in this size.',
    notHere: 'Not on the shelf in any of our shops right now.',
    stockedAt: '{shop} stocks this line, so they may be able to get your size in.',
    directions: 'Directions',
    otherShops: 'Other shops',
    book: 'Book a fitting',
    bookAt: 'Book a fitting at {shop}',
    bookLead: 'Pick a time and it is yours — the confirmation is emailed at once.',
    yourName: 'Your name',
    yourEmail: 'Email',
    yourPhone: 'Phone (optional)',
    day: 'Which day',
    time: 'What time',
    noDays: 'No fitting times left — ring the shop and they will sort something out.',
    note: 'Anything else? (optional)',
    send: 'Pick a time',
    sending: 'Sending…',
    booked: 'Asked for. {shop} will email you to confirm the time.',
    failed: 'That did not send. Please try again, or ring the shop.',
    cancel: 'Cancel',
    loading: 'Reading the diary…',
    noDiary: 'We cannot reach the diary just now. Please ring the shop and they will book you in.',
    people: 'How many of you',
    lasts: 'We will keep {minutes} minutes for you.',
    timeMoved: '{was} is not long enough for {minutes} minutes, so we have moved you to {now}.',
    changeDay: 'Change day',
    dayFull: 'That day cannot take {minutes} minutes any more. Pick another.',
    groupLonger: 'From {from} people it is {minutes} minutes.',
    time: 'What time',
    earlier: 'Earlier month',
    later: 'Later month',
    pickTime: 'Pick a day and a time.',
    needName: 'Tell us your name so the shop knows who to expect.',
    needEmail: 'We need an email address to send the confirmation to.',
    confirmAt: 'Book {time} — confirmed at once',
    taken: 'Somebody has just taken that time. Pick another and it is yours.',
    booked: 'Booked.',
    bookedDetail: '{shop} — {when}',
    emailSent: 'The confirmation is on its way to {email}.',
    emailLater: 'We have you in the diary. The confirmation email did not go, so the shop will be in touch.',
    bookLeadSize: 'We will have your {size} out and ready.',
    /* Not "live". The shelf reaches Shopify from Linnworks every 10–15
       minutes and this endpoint caches for five, so the honest claim is a
       quarter of an hour, not this second. */
    inStock: 'In stock',
    notInStock: 'Not on the shelf',
    updatedEvery: 'updated every 15 min',
    alsoCollect: 'Collect here too — {time}',
    ringFirst: 'Worth ringing first.',
    /* English only until they go through the admin translator: a `t` key
       that is not in the locale files prints "Translation missing". */
    shop: 'Which shop',
    arrange: 'Your size is not on the shelf in {shop}, so we need 1–2 working days to bring it in. It can be ready for you from {day}.',
    arrangeOn: 'Your size is not on the shelf in {shop}, so we need 1–2 working days to bring it in. It will be ready for you on {day}.'
  };

  function init(root) {
    if (root.__tcTryOnReady) return;
    root.__tcTryOnReady = true;

    var config = {};
    var configEl = root.parentNode.querySelector('[data-tcfr-config]') ||
                   document.querySelector('[data-tcfr-config]');
    if (configEl) { try { config = JSON.parse(configEl.textContent) || {}; } catch (e) { config = {}; } }

    var SHELF = config.shelfEndpoint;
    var BOOK = config.fittingEndpoint;
    var HANDLE = config.handle;
    if (!SHELF || !HANDLE) return;

    /* Shopify's translation filter HTML-escapes every value, which is right
       for text in the page and wrong for words handed to a script: "&"
       arrives as "&amp;" and an apostrophe as "&#39;". Undone once, here. */
    function unescape(words) {
      var out = {};
      Object.keys(words || {}).forEach(function (k) {
        var v = words[k];
        out[k] = typeof v === 'string'
          ? v.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&#x27;/g, "'")
          : v;
      });
      return out;
    }

    /* The page's words over the script's English, so a key nobody has
       translated yet reads in English rather than as a gap. */
    var given = unescape(config.tryOnStrings || {});
    Object.keys(given).forEach(function (k) { if (!given[k]) delete given[k]; });
    var T = Object.assign({}, EN, given);
    var say = function (key, values) {
      return String(T[key] || EN[key] || '').replace(/\{(\w+)\}/g, function (_, k) {
        return values && values[k] != null ? values[k] : '';
      });
    };

    var host = root.querySelector('[data-tc-tryon]');
    if (!host) return;

    /* Shops the customer is never offered. Empty by default — Shopify's own
       local-pickup switch is the place to say this, and the reasoning is
       written out in tc-pickup.js, which honours the same list in the pickup
       drawer. Dropped from the shelf the moment it arrives, so nothing
       downstream — the dots, the line, the suggestion — has to remember. */
    var HIDDEN = String(config.shopsHidden == null ? '' : config.shopsHidden)
      .split(',')
      .map(function (name) { return name.trim().toLowerCase(); })
      .filter(Boolean);

    function withoutHidden(data) {
      var drop = {};
      var shops = (data.shops || []).filter(function (shop) {
        var name = String(shop.name || '').toLowerCase();
        var out = HIDDEN.some(function (one) { return name.indexOf(one) !== -1; });
        if (out) drop[shop.id] = true;
        return !out;
      });
      var counts = {};
      /* A hidden shop's stock is still stock the shops can bring in, so it
         joins the warehouse's rather than vanishing. */
      var elsewhere = {};
      Object.keys(data.elsewhere || {}).forEach(function (variantId) {
        elsewhere[variantId] = data.elsewhere[variantId] || 0;
      });
      Object.keys(data.counts || {}).forEach(function (variantId) {
        var per = {};
        Object.keys(data.counts[variantId] || {}).forEach(function (shopId) {
          if (!drop[shopId]) per[shopId] = data.counts[variantId][shopId];
          else elsewhere[variantId] = (elsewhere[variantId] || 0) + (data.counts[variantId][shopId] || 0);
        });
        counts[variantId] = per;
      });
      return { handle: data.handle, shops: shops, counts: counts, elsewhere: elsewhere };
    }

    /* The variant map the panel already publishes: each variant's id and its
       options in the order the pickers are indexed. Lost once to an edit that
       replaced the block above it and took this with it, which left the whole
       feature throwing "variants is not defined" behind a silent catch — so
       the block simply never appeared. */
    var variants = [];
    var variantJson = root.parentNode.querySelector('[data-tcfr-variants]') ||
                      document.querySelector('[data-tcfr-variants]');
    if (variantJson) {
      try { variants = JSON.parse(variantJson.textContent) || []; } catch (e) { variants = []; }
    }

    var shelf = null;

    /* ── which picker is the size, and what is chosen ─────────────── */

    function selectors() {
      return [].slice.call(root.querySelectorAll('.option-selector'));
    }

    /* "Size" in the shop's languages. The legend is translated, so a
       Romanian page says "Mărime" — missing it sent Cristian's booking to
       the shop with no size (22 Oct 2026). */
    var SIZE_WORDS = ['size', 'größe', 'grösse', 'taille', 'mărime', 'marime', 'rozmiar', 'talla',
      'taglia', 'maat', 'tamanho', 'storlek', 'størrelse', 'koko', 'velikost', 'veľkosť', 'méret',
      'μέγεθος', 'размер', 'розмір', 'beden', 'izmērs', 'dydis', 'suurus', 'veličina', 'velikost'];

    /** The zero-based option position of the picker whose legend says size. */
    function sizeIndex() {
      var all = selectors();
      for (var i = 0; i < all.length; i++) {
        var name = (all[i].getAttribute('data-option') || all[i].textContent || '').toLowerCase();
        if (SIZE_WORDS.some(function (w) { return name.indexOf(w) !== -1; })) {
          return (+all[i].getAttribute('data-index') || i + 1) - 1;
        }
      }
      /* A product with one picker: that picker is the size. */
      if (all.length === 1) return (+all[0].getAttribute('data-index') || 1) - 1;
      return -1;
    }

    /** Every option's chosen value, by picker position. */
    function chosen() {
      var out = [];
      selectors().forEach(function (sel) {
        var at = (+sel.getAttribute('data-index') || 0) - 1;
        var radio = sel.querySelector('input[type="radio"]:checked');
        var select = sel.querySelector('select');
        var hidden = sel.querySelector('input[type="hidden"]');
        var value = radio ? radio.value : select ? select.value : hidden ? hidden.value : '';
        if (at >= 0) out[at] = value;
      });
      return out;
    }

    function variantFor(options) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], same = true;
        for (var k = 0; k < options.length; k++) {
          if (options[k] == null) continue;
          if (String((v.options || [])[k]) !== String(options[k])) { same = false; break; }
        }
        if (same) return v;
      }
      return null;
    }

    /** Shops holding a variant, most stock first. */
    function shopsWith(variantId) {
      if (!shelf || !variantId) return [];
      var counts = shelf.counts[String(variantId)] || {};
      return (shelf.shops || [])
        .filter(function (s) { return counts[s.id] > 0; })
        .map(function (s) { return { shop: s, count: counts[s.id] }; })
        .sort(function (a, b) { return b.count - a.count; });
    }

    /* ── the dots ─────────────────────────────────────────────────────
       A dot on a size label means: you could walk in and try this one.
       Placed on the label rather than drawn as a legend, because the sizes
       are where a customer's eye already is. */

    function markSizes() {
      var at = sizeIndex();
      if (at < 0 || !shelf) return;
      var picked = chosen();
      var group = selectors().filter(function (sel) {
        return (+sel.getAttribute('data-index') || 0) - 1 === at;
      })[0];
      if (!group) return;

      group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        var options = picked.slice();
        options[at] = radio.value;
        var v = variantFor(options);
        var has = v && shopsWith(v.id).length > 0;
        var label = group.querySelector('label[for="' + radio.id + '"]');
        if (!label) return;
        label.classList.toggle('tc-tryon-shelf', !!has);
        if (has) label.setAttribute('data-tc-shelf-title', say('onShelf'));
        else label.removeAttribute('data-tc-shelf-title');
      });
      group.classList.toggle('tc-tryon-legend', true);
    }

    /* ── the block ──────────────────────────────────────────────────── */

    /* Street and postcode. The address arrives as Shopify files it —
       street, then "London, England", then the postcode, then the country —
       and the county is no help to somebody deciding whether to walk. */
    var UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;
    function shortAddress(shop) {
      var parts = shop.address || [];
      if (!parts.length) return '';
      var street = parts[0];
      var postcode = null;
      for (var i = 1; i < parts.length; i++) {
        if (UK_POSTCODE.test(String(parts[i]).trim())) { postcode = parts[i]; break; }
      }
      return postcode ? street + ', ' + postcode : parts.slice(0, 2).join(', ');
    }

    /* Shopify files shop numbers as +442030268793; a customer reads
       020 3026 8793. Only London numbers are reshaped — anything else is
       shown as Shopify has it. */
    function phoneText(phone) {
      var m = String(phone || '').replace(/\s+/g, '').match(/^\+44(20)(\d{4})(\d{4})$/);
      return m ? '0' + m[1] + ' ' + m[2] + ' ' + m[3] : String(phone || '');
    }

    var mapsFor = function (shop) {
      return 'https://www.google.com/maps/search/?api=1&query=' +
        encodeURIComponent([shop.name].concat(shop.address || []).join(', '));
    };

    /**
     * A size that breaks only where it should.
     *
     * A suit size is two measurements — 38" Jacket 32" Trousers — and a
     * column this narrow was breaking it between the 38" and the word
     * Jacket, which reads as two different sizes. A hard space inside each
     * half leaves the space between the halves as the only place a line can
     * break, so the worst case is one measurement per line.
     */
    function tidySize(size) {
      return String(size || '').replace(/(\d+\s*["”′]?)\s+/g, '$1\u00a0');
    }

    function line(node, html) {
      var p = document.createElement('p');
      p.className = 'tc-tryon__line';
      p.innerHTML = html;
      node.appendChild(p);
    }

    /* The old pickup line is hidden only while this block is actually
       showing: on a product no shop stocks, this says nothing and Shopify's
       line is the best the page has. */
    function showing(on) {
      root.classList.toggle('tc-tryon-on', !!on);
    }

    function render() {
      if (!shelf || !(shelf.shops || []).length) { host.hidden = true; showing(false); return; }
      var picked = chosen();
      var variant = variantFor(picked);
      var here = variant ? shopsWith(variant.id) : [];
      /* A shop to send them to when their own size is not out: the one with
         most of this line on its shelf, so the suggestion is a shop that
         actually has the suit in other sizes. A zero inventory record is not
         a reason to name a shop. */
      var stocking = (shelf.shops || [])
        .filter(function (s) { return s.lineTotal > 0; })
        .sort(function (a, b) { return b.lineTotal - a.lineTotal; });
      /* Shown even when no shop has the line: any shop can bring it in for a
         fitting now, a couple of working days out, so there is still
         something to offer — unless nobody has it at all, shops or
         warehouse, and then there is nothing to bring in and no Book button
         (Maciek, 22 Oct 2026; the server refuses it too). */
      var gettable = variant
        ? here.length > 0 || (shelf.elsewhere && shelf.elsewhere[variant.id] > 0)
        : stocking.length > 0 || Object.keys(shelf.elsewhere || {}).some(function (id) { return shelf.elsewhere[id] > 0; });

      var size = picked[sizeIndex()] || '';
      host.hidden = false;
      showing(true);
      host.innerHTML = '';

      var head = document.createElement('div');
      head.className = 'tc-tryon__top';
      var h = document.createElement('p');
      h.className = 'tc-tryon__head';
      h.textContent = say('tryOn') + (size ? ' · ' + tidySize(size) : '');
      /* The answer, then how old the answer is.
         ------------------------------------------------------------------
         What a customer wants from the corner of this panel is yes or no, so
         that is the line they read: a tick and "In stock" when their own size
         is out on the shelf. How fresh the number is matters too — the shelf
         reaches us from the till every quarter of an hour — but it is a
         footnote to the answer, not half of it, so it sits underneath in
         small type. */
      var live = document.createElement('span');
      live.className = 'tc-tryon__live';

      var state = document.createElement('span');
      state.className = 'tc-tryon__state' + (here.length ? ' is-in' : '');
      if (here.length) {
        state.innerHTML =
          '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
          '<path d="m16.558 4.8.884.884-9.959 9.958-4.925-4.925.884-.884 4.041 4.041z"/></svg>';
      }
      state.appendChild(document.createTextNode(here.length ? say('inStock') : say('notInStock')));

      var since = document.createElement('span');
      since.className = 'tc-tryon__since';
      since.textContent = say('updatedEvery');

      live.appendChild(state);
      live.appendChild(since);
      head.appendChild(h);
      head.appendChild(live);
      host.appendChild(head);

      var shop = here.length ? here[0].shop : (stocking[0] || shelf.shops[0]);
      if (here.length) {
        var count = here[0].count;
        /* One left plus a quarter of an hour of lag is the case where a
           customer can arrive to find it gone, so that is the one time the
           page says to ring — the number is already in the row below. */
        line(host,
          say('shelfAt', { size: '<strong>' + escapeHtml(tidySize(size)) + '</strong>', shop: '<strong>' + escapeHtml(shop.name) + '</strong>' }) +
          (shortAddress(shop) ? ', ' + escapeHtml(shortAddress(shop)) : '') + '. ' +
          (count === 1 ? say('lastOne') + (shop.phone ? ' ' + say('ringFirst') : '') : say('someLeft', { count: count })));
      } else {
        line(host, say('notHere') +
          (stocking.length ? ' ' + say('stockedAt', { shop: '<strong>' + escapeHtml(shop.name) + '</strong>' }) : ''));
      }

      /* What Shopify's pickup line used to say. It is the same fact from the
         same place — the shop's local pickup settings — so with it said here
         that line is redundant, and the class below hides it. */
      if (here.length && shop.pickupTime) {
        var ready = document.createElement('p');
        ready.className = 'tc-tryon__ready';
        ready.textContent = say('alsoCollect', { time: String(shop.pickupTime).replace(/^Usually\s+/i, 'usually ') });
        host.appendChild(ready);
      }

      var foot = document.createElement('div');
      foot.className = 'tc-tryon__foot';

      var book = document.createElement('button');
      book.type = 'button';
      book.className = 'tc-tryon__book';
      book.textContent = say('book');
      if (BOOK && gettable) foot.appendChild(book);

      var directions = document.createElement('a');
      directions.className = 'tc-tryon__link';
      directions.href = mapsFor(shop);
      directions.target = '_blank';
      directions.rel = 'noopener';
      directions.textContent = say('directions');
      foot.appendChild(directions);

      if (shop.phone) {
        var tel = document.createElement('a');
        tel.className = 'tc-tryon__link';
        tel.href = 'tel:' + shop.phone.replace(/[^+\d]/g, '');
        tel.textContent = phoneText(shop.phone);
        foot.appendChild(tel);
      }

      /* Every shop, in the drawer the page already has: tc-pickup.js fills it
         with the shops Shopify left out, so there is no second list to build
         or keep in step.

         Opened on the drawer itself rather than by clicking Shopify's button,
         because that button is inside the pickup line this block replaces and
         is no longer on screen. `side-drawer` is Symmetry's own element and
         `open(opener)` is its own method — it takes the element to return
         focus to when the drawer closes, which is this button. */
      var drawer = document.querySelector('side-drawer[id^="pickup-availability"]');
      if (drawer && shelf.shops.length > 1) {
        var others = document.createElement('button');
        others.type = 'button';
        others.className = 'tc-tryon__link tc-tryon__link--button';
        others.textContent = say('otherShops');
        others.setAttribute('aria-haspopup', 'dialog');
        others.addEventListener('click', function () {
          var live = document.querySelector('side-drawer[id^="pickup-availability"]');
          if (!live) return;
          fillDrawer(live, variant, picked);
          if (typeof live.open === 'function') live.open(others);
          else {
            /* If Symmetry ever renames that method, the button Shopify
               rendered is still in the DOM, hidden, and still works. */
            var fallback = document.querySelector('.js-show-pickup-info');
            if (fallback) fallback.click();
          }
        });
        foot.appendChild(others);
      }

      host.appendChild(foot);

      book.addEventListener('click', function () {
        openBooking(book, shop, variant, size, here.length ? here[0].count : 0);
      });
    }

    /* The drawer, rewritten for the size on the screen.
       ------------------------------------------------------------------
       Shopify renders the drawer once, for the variant the page opened on,
       and Symmetry never re-renders it when the size changes. So a page that
       opened on a 34" held at Plaistow went on saying "Plaistow — In stock,
       Croydon — Not here" under a 36" that only Croydon has. The shelf is
       per variant and already knows about Plaistow's warehouse stock
       (not-plaistow-shelf), so the list is rebuilt from it every time the
       drawer is opened from here. Shops with the size come first. */
    function fillDrawer(drawer, variant, picked) {
      var list = drawer.querySelector('ul');
      if (!list || !shelf) return;

      drawer.querySelectorAll('.drawer__header p span').forEach(function (value, i) {
        if (picked[i] != null) value.textContent = picked[i];
      });

      var counts = variant ? (shelf.counts[String(variant.id)] || {}) : {};
      var rows = (shelf.shops || []).slice().sort(function (a, b) {
        return ((counts[b.id] || 0) > 0) - ((counts[a.id] || 0) > 0);
      });

      list.innerHTML = '';
      rows.forEach(function (s) {
        var has = (counts[s.id] || 0) > 0;
        var li = document.createElement('li');
        li.className = 'tc-store' + (has ? ' is-available' : '');

        var head = document.createElement('div');
        head.className = 'tc-store__head';
        var name = document.createElement('span');
        name.className = 'tc-store__name';
        name.setAttribute('role', 'heading');
        name.setAttribute('aria-level', '3');
        name.textContent = s.name;
        head.appendChild(name);
        var tag = document.createElement('span');
        tag.className = 'tc-store__tag ' + (has ? 'tc-store__tag--yes' : 'tc-store__tag--no');
        tag.textContent = has ? 'In stock' : 'Not here';
        head.appendChild(tag);
        li.appendChild(head);

        if (has && s.pickupTime) {
          var ready = document.createElement('p');
          ready.className = 'tc-store__ready';
          ready.textContent = s.pickupTime;
          li.appendChild(ready);
        }

        var where = document.createElement('p');
        where.className = 'tc-store__where';
        where.textContent = shortAddress(s);
        li.appendChild(where);

        if (s.phone) {
          var tel = document.createElement('a');
          tel.className = 'tc-store__tel';
          tel.href = 'tel:' + String(s.phone).replace(/[^+\d]/g, '');
          tel.textContent = phoneText(s.phone);
          li.appendChild(tel);
        }

        list.appendChild(li);
      });
      /* tc-pickup.js has nothing left to add, and must not try. */
      drawer.__tcFilled = true;
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    /* ── booking a fitting, in the drawer ─────────────────────────────
       A calendar, the times that day still has, and how many are coming.
       The slot rules come from the API with the diary — how long a fitting
       for four takes is the shop's policy, and the page having its own copy
       of it is how the two quietly disagree.

       The whole thing lives in Symmetry's `side-drawer` so it opens and
       behaves like every other drawer on the site. */

    var BOOK_SLOTS = config.fittingSlotsEndpoint;
    var drawerEl = null;
    var booking = null;

    function field(parent, label, attrs) {
      var wrap = document.createElement('label');
      wrap.className = 'tc-tryon__field';
      var span = document.createElement('span');
      span.textContent = label;
      var input = document.createElement(attrs.tag || 'input');
      Object.keys(attrs).forEach(function (k) {
        if (k !== 'tag') input.setAttribute(k, attrs[k]);
      });
      wrap.appendChild(span);
      wrap.appendChild(input);
      parent.appendChild(wrap);
      return input;
    }

    function chipRow(parent, label) {
      var wrap = document.createElement('div');
      wrap.className = 'tc-tryon__field';
      var span = document.createElement('span');
      span.textContent = label;
      wrap.appendChild(span);
      var row = document.createElement('div');
      row.className = 'tc-tryon__days';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', label);
      wrap.appendChild(row);
      parent.appendChild(wrap);
      return row;
    }

    function chip(row, text, pressed) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tc-tryon__day';
      b.textContent = text;
      b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      row.appendChild(b);
      return b;
    }

    function press(row, one) {
      row.querySelectorAll('.tc-tryon__day').forEach(function (other) {
        other.setAttribute('aria-pressed', other === one ? 'true' : 'false');
      });
    }

    /** Minutes past midnight, for comparing a time against an interval. */
    function mins(hhmm) {
      var p = String(hhmm).split(':');
      return (+p[0]) * 60 + (+p[1]);
    }

    /** The times a day still has, once the diary is taken off it. */
    function freeOn(diary, date, minutes) {
      var day = (diary.days || []).filter(function (d) { return d.date === date; })[0];
      if (!day) return [];
      var taken = (diary.taken || []).filter(function (t) { return t.date === date; });
      return day.slots.filter(function (slot) {
        var from = mins(slot), to = from + minutes;
        return !taken.some(function (t) {
          var tFrom = mins(t.time), tTo = tFrom + Number(t.minutes || 30);
          return from < tTo && tTo > from && tFrom < to && to > tFrom;
        });
      });
    }

    /* The shop's own language for dates, British when it is English: the page
       says plain "en", and a browser reads that as American — "Thursday,
       September 17" for a shop in Plaistow. */
    function dateLocale() {
      var code = config.locale || 'en';
      return /^en$/i.test(code) ? 'en-GB' : code;
    }

    /** "Thursday 17 September", for the line that replaces the calendar. */
    function dayWords(date) {
      var p = String(date).split('-');
      try {
        return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]))
          .toLocaleDateString(dateLocale(), {
            weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
          });
      } catch (e) {
        return date;
      }
    }

    function monthName(year, month) {
      try {
        return new Date(Date.UTC(year, month, 1))
          .toLocaleDateString(dateLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' });
      } catch (e) {
        return year + '-' + (month + 1);
      }
    }

    var iso = function (year, month, day) {
      return year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    };

    function openBooking(opener, shop, variant, size, count) {
      drawerEl = drawerEl || document.querySelector('side-drawer#tc-fitting-drawer');
      if (!drawerEl || !BOOK || !BOOK_SLOTS) return;
      var body = drawerEl.querySelector('[data-tc-fitting-body]');
      var where = drawerEl.querySelector('[data-tc-fitting-where]');
      if (!body) return;

      booking = {
        shop: shop, variant: variant, size: size, count: count,
        people: 1, date: null, time: null, minutes: 30, diary: null
      };
      if (where) where.textContent = shop.name + (shortAddress(shop) ? ' · ' + shortAddress(shop) : '');

      body.textContent = '';
      var loading = document.createElement('p');
      loading.className = 'tc-tryon__form-lead';
      loading.textContent = say('loading');
      body.appendChild(loading);

      if (typeof drawerEl.open === 'function') drawerEl.open(opener);

      loadDiary(body);
    }

    function loadDiary(body) {
      var url = BOOK_SLOTS +
        (BOOK_SLOTS.indexOf('?') === -1 ? '?' : '&') +
        'shopId=' + encodeURIComponent(booking.shop.id) +
        '&people=' + encodeURIComponent(booking.people) +
        '&handle=' + encodeURIComponent(HANDLE) +
        '&variantId=' + encodeURIComponent(booking.variant ? booking.variant.id : '');
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (diary) {
          if (!diary || !diary.days) throw new Error('no diary');
          booking.diary = diary;
          booking.minutes = diary.minutes || 30;
          /* Nothing is chosen for the customer. A day they have already
             picked is kept if it can still take the fitting — a party of
             six may not fit where a party of two did, and then the panel
             says so and goes back to the calendar. */
          var open = diary.days.filter(function (d) { return freeOn(diary, d.date, booking.minutes).length; });
          if (booking.date && !open.some(function (d) { return d.date === booking.date; })) {
            booking.said = say('dayFull', { minutes: booking.minutes });
            booking.date = null;
            booking.time = null;
          }
          var free = booking.date ? freeOn(diary, booking.date, booking.minutes) : [];
          if (booking.time && free.indexOf(booking.time) === -1) {
            booking.moved = booking.wanted || booking.time;
            booking.time = free[0] || null;
          } else if (!booking.time) {
            booking.moved = null;
          }
          booking.wanted = null;
          drawBooking(body);
        })
        .catch(function () {
          body.textContent = '';
          var bad = document.createElement('p');
          bad.className = 'tc-tryon__msg is-bad';
          bad.textContent = say('noDiary');
          body.appendChild(bad);
        });
    }

    /**
     * The panel, in two steps.
     *
     * How many are coming sits at the top, because the length of a fitting
     * follows from it and the length decides which times exist at all. Then
     * a date, and nothing else: a wall of times for a fortnight is not a
     * question anybody can answer. Choosing the day puts the calendar away
     * and shows that day's hours, with the date itself as the way back.
     */
    function drawBooking(body) {
      var diary = booking.diary;
      body.textContent = '';

      var lead = document.createElement('p');
      lead.className = 'tc-tryon__form-lead';
      lead.textContent = booking.size ? say('bookLeadSize', { size: tidySize(booking.size) }) : say('bookLead');
      body.appendChild(lead);

      /* Which shop. The one with the size out is offered first, but any of
         them can bring it in — the diary then starts from the day it can be
         there, which the API decides. */
      if (shelf && (shelf.shops || []).length > 1) {
        var shopRow = chipRow(body, say('shop'));
        shelf.shops.forEach(function (s) {
          var one = chip(shopRow, s.shortName || s.name, s.id === booking.shop.id);
          one.addEventListener('click', function () {
            if (booking.shop.id === s.id) return;
            booking.shop = s;
            booking.count = booking.variant ? ((shelf.counts[String(booking.variant.id)] || {})[s.id] || 0) : 0;
            booking.date = null;
            booking.time = null;
            booking.said = null;
            booking.moved = null;
            booking.wanted = null;
            var where = drawerEl && drawerEl.querySelector('[data-tc-fitting-where]');
            if (where) where.textContent = s.name + (shortAddress(s) ? ' · ' + shortAddress(s) : '');
            press(shopRow, one);
            loadDiary(body);
          });
        });
      }

      if (diary.arrange && diary.arrange.earliest) {
        var arrange = document.createElement('p');
        arrange.className = 'tc-tryon__arrange';
        /* How long, then when: the first day it can be ready until they pick
           one, and the day they picked once they have. */
        arrange.textContent = booking.date
          ? say('arrangeOn', { shop: booking.shop.shortName || booking.shop.name, day: dayWords(booking.date) })
          : say('arrange', { shop: booking.shop.shortName || booking.shop.name, day: dayWords(diary.arrange.earliest) });
        body.appendChild(arrange);
      }

      /* First, because the times depend on it. */
      var rules = diary.rules || {};
      var peopleRow = chipRow(body, say('people'));
      [1, 2, 3, 4, 5, 6].forEach(function (n) {
        var one = chip(peopleRow, n === 6 ? '6+' : String(n), n === booking.people);
        one.addEventListener('click', function () {
          if (booking.people === n) return;
          booking.people = n;
          press(peopleRow, one);
          booking.wanted = booking.time;
          loadDiary(body);
        });
      });

      var howLong = document.createElement('p');
      howLong.className = 'tc-tryon__ready';
      var tiers = (rules.lengths || []).slice().sort(function (a, b) { return a.from - b.from; });
      var next = tiers.filter(function (t) { return t.from > booking.people; })[0];
      howLong.textContent = say('lasts', { minutes: booking.minutes }) +
        (next ? ' ' + say('groupLonger', { from: next.from, minutes: next.minutes }) : '');
      body.appendChild(howLong);

      /* Which days could still take a fitting this long. */
      var openDates = {};
      (diary.days || []).forEach(function (d) {
        if (freeOn(diary, d.date, booking.minutes).length) openDates[d.date] = true;
      });
      var dateList = Object.keys(openDates).sort();
      if (!dateList.length) {
        var none = document.createElement('p');
        none.className = 'tc-tryon__msg';
        none.textContent = say('noDays');
        body.appendChild(none);
        return;
      }

      if (booking.said) {
        var said = document.createElement('p');
        said.className = 'tc-tryon__msg is-bad';
        said.textContent = booking.said;
        body.appendChild(said);
        booking.said = null;
      }

      if (!booking.date) {
        drawCalendar(body, openDates, dateList);
        return;
      }

      /* ── the day is chosen ─────────────────────────────────────────── */

      var chosen = document.createElement('div');
      chosen.className = 'tc-tryon__chosen';
      var chosenDay = document.createElement('strong');
      chosenDay.textContent = dayWords(booking.date);
      var change = document.createElement('button');
      change.type = 'button';
      change.className = 'tc-tryon__link tc-tryon__link--button';
      change.textContent = say('changeDay');
      change.addEventListener('click', function () {
        booking.date = null;
        booking.time = null;
        drawBooking(body);
      });
      chosen.appendChild(chosenDay);
      chosen.appendChild(change);
      body.appendChild(chosen);

      var free = freeOn(diary, booking.date, booking.minutes);
      var timeRow = chipRow(body, say('time'));
      free.forEach(function (slot) {
        var one = chip(timeRow, slot, slot === booking.time);
        one.addEventListener('click', function () {
          booking.time = slot;
          booking.moved = null;
          press(timeRow, one);
          if (send) {
            send.disabled = false;
            send.textContent = label();
          }
        });
      });
      if (booking.moved && booking.time) {
        var shifted = document.createElement('p');
        shifted.className = 'tc-tryon__ready';
        shifted.textContent = say('timeMoved', { was: booking.moved, now: booking.time, minutes: booking.minutes });
        body.appendChild(shifted);
      }

      /* Who to expect. */
      var name = field(body, say('yourName'), { type: 'text', autocomplete: 'name', required: 'required', id: 'tc-fitting-name' });
      var email = field(body, say('yourEmail'), { type: 'email', autocomplete: 'email', required: 'required', id: 'tc-fitting-email' });
      var phone = field(body, say('yourPhone'), { type: 'tel', autocomplete: 'tel', id: 'tc-fitting-phone' });
      var note = field(body, say('note'), { tag: 'textarea', rows: '2', id: 'tc-fitting-note' });

      var foot = document.createElement('div');
      foot.className = 'tc-tryon__form-foot';
      var send = document.createElement('button');
      send.type = 'button';
      send.className = 'tc-tryon__send';
      var label = function () {
        return booking.time ? say('confirmAt', { time: booking.time }) : say('send');
      };
      send.textContent = label();
      /* Nothing is preselected, so until an hour is chosen the button says
         what is missing rather than offering to book nothing. */
      send.disabled = !booking.time;
      foot.appendChild(send);
      body.appendChild(foot);

      var msg = document.createElement('p');
      msg.className = 'tc-tryon__msg';
      msg.hidden = true;
      msg.setAttribute('role', 'status');
      body.appendChild(msg);

      send.addEventListener('click', function () {
        if (!booking.date || !booking.time) {
          msg.hidden = false;
          msg.className = 'tc-tryon__msg is-bad';
          msg.textContent = say('pickTime');
          return;
        }
        if (!name.value.trim() || !email.checkValidity()) {
          msg.hidden = false;
          msg.className = 'tc-tryon__msg is-bad';
          msg.textContent = !name.value.trim() ? say('needName') : say('needEmail');
          (name.value.trim() ? email : name).focus();
          return;
        }
        send.disabled = true;
        send.textContent = say('sending');
        msg.hidden = true;

        fetch(BOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shopId: booking.shop.id,
            handle: HANDLE,
            productTitle: config.productTitle || document.title,
            variantId: booking.variant ? String(booking.variant.id) : '',
            size: booking.size,
            sku: booking.variant ? booking.variant.sku || '' : '',
            onShelf: booking.count,
            people: booking.people,
            date: booking.date,
            time: booking.time,
            name: name.value,
            email: email.value,
            phone: phone.value,
            note: note.value,
            locale: config.locale || 'en'
          })
        })
          .then(function (r) {
            return r.json().catch(function () { return {}; }).then(function (b) { return { status: r.status, ok: r.ok, body: b }; });
          })
          .then(function (answer) {
            if (answer.status === 409) {
              /* Taken while this page was open. The diary has moved on, so
                 it is re-read rather than argued with. */
              booking.said = answer.body.error || say('taken');
              booking.time = null;
              loadDiary(body);
              return;
            }
            if (!answer.ok) throw new Error(answer.body && answer.body.error || 'failed');
            body.textContent = '';
            var done = document.createElement('p');
            done.className = 'tc-tryon__form-head';
            done.textContent = say('booked');
            var detail = document.createElement('p');
            detail.className = 'tc-tryon__line';
            detail.textContent = say('bookedDetail', {
              shop: answer.body.shop || booking.shop.name,
              when: answer.body.when || ''
            });
            var mail = document.createElement('p');
            mail.className = 'tc-tryon__ready';
            mail.textContent = answer.body.emailed ? say('emailSent', { email: email.value }) : say('emailLater');
            body.appendChild(done);
            body.appendChild(detail);
            body.appendChild(mail);
          })
          .catch(function (err) {
            send.disabled = false;
            send.textContent = label();
            msg.hidden = false;
            msg.className = 'tc-tryon__msg is-bad';
            msg.textContent = (err && err.message && err.message !== 'failed') ? err.message : say('failed');
          });
      });
    }

    /** The month, with only the days that can still take this fitting. */
    function drawCalendar(body, openDates, dateList) {
      var shown = dateList[0];
      var cal = document.createElement('div');
      cal.className = 'tc-cal';
      body.appendChild(cal);

      function drawMonth(year, month) {
        cal.textContent = '';

        var head = document.createElement('div');
        head.className = 'tc-cal__head';
        var back = document.createElement('button');
        back.type = 'button';
        back.className = 'tc-cal__step';
        back.setAttribute('aria-label', say('earlier'));
        back.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg>';
        var title = document.createElement('span');
        title.className = 'tc-cal__month';
        title.textContent = monthName(year, month);
        var on = document.createElement('button');
        on.type = 'button';
        on.className = 'tc-cal__step';
        on.setAttribute('aria-label', say('later'));
        on.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';
        head.appendChild(back);
        head.appendChild(title);
        head.appendChild(on);
        cal.appendChild(head);

        var first = dateList[0], last = dateList[dateList.length - 1];
        var here = year + '-' + String(month + 1).padStart(2, '0');
        back.disabled = here <= first.slice(0, 7);
        on.disabled = here >= last.slice(0, 7);
        back.addEventListener('click', function () {
          var d = new Date(Date.UTC(year, month - 1, 1));
          drawMonth(d.getUTCFullYear(), d.getUTCMonth());
        });
        on.addEventListener('click', function () {
          var d = new Date(Date.UTC(year, month + 1, 1));
          drawMonth(d.getUTCFullYear(), d.getUTCMonth());
        });

        var grid = document.createElement('div');
        grid.className = 'tc-cal__grid';
        /* Monday first: these are British shops. */
        var names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        var localNames = names;
        try {
          localNames = [1, 2, 3, 4, 5, 6, 7].map(function (dow) {
            return new Date(Date.UTC(2024, 0, dow))
              .toLocaleDateString(dateLocale(), { weekday: 'narrow', timeZone: 'UTC' });
          });
        } catch (e) { localNames = names; }
        localNames.forEach(function (n) {
          var dow = document.createElement('span');
          dow.className = 'tc-cal__dow';
          dow.textContent = n;
          grid.appendChild(dow);
        });

        var firstOfMonth = new Date(Date.UTC(year, month, 1));
        var blanks = (firstOfMonth.getUTCDay() + 6) % 7;
        for (var b = 0; b < blanks; b++) {
          var pad = document.createElement('span');
          pad.className = 'tc-cal__pad';
          grid.appendChild(pad);
        }
        var days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
        for (var day = 1; day <= days; day++) {
          var date = iso(year, month, day);
          var cell = document.createElement('button');
          cell.type = 'button';
          cell.className = 'tc-cal__day';
          cell.textContent = String(day);
          if (!openDates[date]) {
            cell.disabled = true;
            cell.setAttribute('aria-disabled', 'true');
          } else {
            cell.setAttribute('aria-label', dayWords(date));
            (function (thisDate) {
              cell.addEventListener('click', function () {
                booking.date = thisDate;
                /* No hour is assumed: the next step is the one question
                   left, and a preselected chip invites nobody to read it. */
                booking.time = null;
                drawBooking(body);
              });
            })(date);
          }
          grid.appendChild(cell);
        }
        cal.appendChild(grid);
      }

      drawMonth(+shown.slice(0, 4), +shown.slice(5, 7) - 1);
    }

    /* ── go ──────────────────────────────────────────────────────────── */

    fetch(SHELF.replace('{handle}', encodeURIComponent(HANDLE)), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.shops) return;
        shelf = withoutHidden(data);
        markSizes();
        render();
      })
      .catch(function (err) {
        /* The page stands as rendered — but not silently. A swallowed
           exception here cost an afternoon: the block was simply absent and
           there was nothing anywhere to say why. */
        if (window.console && console.warn) console.warn('[tc-tryon]', err && err.message ? err.message : err);
      });

    root.addEventListener('change', function (e) {
      if (!e.target.closest || !e.target.closest('.option-selector')) return;
      setTimeout(function () { markSizes(); render(); }, 0);
    });
    document.addEventListener('variant:change', function () {
      setTimeout(function () { markSizes(); render(); }, 0);
    });
  }

  function boot() {
    document.querySelectorAll('[data-tcfr]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
